/**
 * Frontend mirror of backend/src/utils/money.js (owner decision 2026-09-11:
 * every unit price is entered, stored and displayed with 4 decimal places,
 * half-up rounded; amounts/totals stay 2 decimal places).
 *
 * Keep this file's rounding/line-value math identical to the backend
 * implementation. Only formatting helpers (which reuse numberFormatting.ts)
 * are frontend-specific.
 */
import { formatLocalizedNumber, localeForLanguage } from './numberFormatting';

export const UNIT_PRICE_DECIMALS = 4;
export const MONEY_DECIMALS = 2;

const DECIMAL_EPSILON = 1e-9;

export function roundDecimalHalfUp(value: unknown, decimals: number): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return Number.NaN;
  const rounded = Number(`${Math.round(Number(`${numeric}e${decimals}`))}e-${decimals}`);
  return Number.isFinite(rounded)
    ? rounded
    : Math.round(numeric * (10 ** decimals)) / (10 ** decimals);
}

export function roundUnitPrice(value: unknown): number {
  return roundDecimalHalfUp(value, UNIT_PRICE_DECIMALS);
}

export function roundMoney(value: unknown): number {
  return roundDecimalHalfUp(value, MONEY_DECIMALS);
}

export function hasUnitPricePrecision(value: unknown): boolean {
  const numeric = Number(value);
  const rounded = roundUnitPrice(value);
  return Number.isFinite(numeric) && Number.isFinite(rounded) && Math.abs(numeric - rounded) < DECIMAL_EPSILON;
}

function toScaledInteger(value: unknown, decimals: number): bigint | null {
  const rounded = roundDecimalHalfUp(value, decimals);
  if (!Number.isFinite(rounded) || rounded < 0) return null;
  return BigInt(rounded.toFixed(decimals).replace('.', ''));
}

/** Exact 3dp quantity × 4dp unit price, rounded half-up to monetary cents. */
export function calculateLineValue(pricingQuantity: unknown, unitPrice: unknown): number {
  const quantityScaled = toScaledInteger(pricingQuantity, 3);
  const priceScaled = toScaledInteger(unitPrice, UNIT_PRICE_DECIMALS);
  if (quantityScaled === null || priceScaled === null) return Number.NaN;
  const rawScaled = quantityScaled * priceScaled; // value × 10^7
  const cents = (rawScaled + BigInt(50000)) / BigInt(100000);
  return Number(cents) / 100;
}

/**
 * Localized unit price display: up to 4 decimal places, never fewer than 2,
 * with trailing zeros beyond 2dp trimmed (0.333 -> "0,333", 5 -> "5,00").
 */
export function formatUnitPrice(value: number | null | undefined, language?: string): string {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  return safeValue.toLocaleString(localeForLanguage(language), {
    minimumFractionDigits: 2,
    maximumFractionDigits: UNIT_PRICE_DECIMALS,
  });
}

/** Localized money/amount display: always 2 decimal places. */
export function formatMoney(value: number | null | undefined, language?: string): string {
  return formatLocalizedNumber(value, MONEY_DECIMALS, language);
}

export const UNIT_PRICE_INPUT_PROPS = { precision: 4, step: 0.0001, min: 0 } as const;
